import ipRegex from 'ip-regex';
import urlRegexSafe from 'url-regex-safe';
import { v4 as uuidv4 } from 'uuid';

import { IUrlParams, IFetchOutput, IFetchFileOutput, IFetchError, IFetchError422, IServerError422, IQuickCommentParseData } from '~/interfaces';
import { REQUEST_TIMEOUT, ROUTE_PARAMS, FETCH_DEF_ERROR, API_PATH, MAX_YEAR, PRICE_CREDIT_RATIO, PENNY_KC_RATIO, MAX_INT_DESCIPTION_LEN, MAX_USER_DESCIPTION_LEN, CREATE_PROJECT_INFO_LINKS, CATALOG_LINKS, ALL_LANGS, MAX_USER_NOTE_LEN, BLACK_LIST_EMAILS } from '~/const';
import { IClientKey, IClientProject, IClientKeyUpdate } from '~/providers/client-interfaces';
import { IAdminKey, IAdminKeyUpdate, IAdminProject } from '~/providers/admin-interfaces';
import { IProjectBillingHistory, IReportItem, IService, TRestrictionsTypes } from '~/providers/interfaces';
import { instanceId } from '~/utils/sentry';
import { useNonHookTrans } from "~/hooks/useTrans";
import { TReportsTableShowData } from "~/tables/AdminReportsTable";

interface IFetchJsonOptions {
	pretty422?: boolean;
}

interface IGetErrors {
	status: number;
	path: string;
	errors: IFetchError;
	requestData: IFetchError422;
	pretty422: boolean;
}

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/ug;
const API_KEY_REGEX = (/[^\s]{42,}/ug);

export const abortFetchSignal = (timeoutMs: number) => {
	if (typeof window === 'undefined' || window.AbortController === undefined) {
		return undefined;
	}

	const abortController = new AbortController();

	setTimeout(() => abortController.abort(), timeoutMs);

	return abortController.signal;
};

export function formatDate(date: Date, isCzech: boolean): string {
	return new Intl.DateTimeFormat(isCzech ? "cs-CZ" : "en-US").format(date);
}

export function formateDateAndTime(date: Date, isCzech: boolean, noSeconds?: boolean): string {
	return new Intl.DateTimeFormat(isCzech ? "cs-CZ" : "en-US", {
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
		...noSeconds
			? {}
			: {
				second: "numeric",
			},
		hour12: false,
	}).format(date);
}

function getErrors({
	status,
	path,
	errors,
	requestData,
	pretty422 = false,
}: IGetErrors): Array<string> {
	/* eslint-disable no-magic-numbers */
	if (status < 200 || status > 299) {
		if (status === 422 && requestData) {
			if (pretty422) {
				// @ts-ignore
				const errorData422: IServerError422 = requestData;

				// @ts-ignore
				return errorData422.errors.map(error => [`${error.message}: ${error.detail.map(detail => detail.msg || "").join(", ") || ""}`]);
			}

			const allMessages = requestData.detail.map(item => `[${item.loc.join("->")}]: ${item.msg}`);

			if (allMessages.length > 0) {
				return allMessages;
			}
		} else if (Array.isArray(errors)) {
			const allMessages = errors.map(item => item.message || item.middleware || item.traceback).filter(item => item);

			if (allMessages.length > 0) {
				return allMessages;
			} else if (status === 404) {
				return [useNonHookTrans().trans("fetchError.error.404", { endpoint: path })];
			}
		}

		return [useNonHookTrans().trans("fetchError.error.common")];
	}

	return [];
}

export async function fetchJSON<T, Y = any>(path: string, fetchOptions?: any, options?: IFetchJsonOptions): Promise<IFetchOutput<T, Y>> {
	const method = fetchOptions?.method || "GET";
	const correlationId = uuidv4();
	let request = null;

	try {
		request = await fetch(path, {
			headers: {
				"Accept": 'application/json',
				"X-Correlation-Id": correlationId,
				"X-Instance-Id": instanceId,

				...method === "GET" || fetchOptions?.body instanceof FormData
					? {}
					: {
						"Content-Type": "application/json",
					},
			},
			credentials: 'include',
			signal: abortFetchSignal(REQUEST_TIMEOUT),
			...fetchOptions,
		});
		const jsonData = await request.json();
		const errors = getErrors({
			status: request.status,
			path,
			errors: jsonData.errors || [],
			requestData: jsonData,
			pretty422: options?.pretty422,
		});

		return {
			// kdyz T neni null
			data: jsonData.data,
			requestData: jsonData,
			// nechodi vzdy
			lastUpdate: jsonData.lastUpdate || "",
			message: jsonData.message || "",
			status: request.status,
			noErrors: errors.length === 0,
			errors,
		};
	} catch (exc) {
		/* eslint-disable no-console */
		return {
			data: null,
			requestData: null,
			status: request?.status || FETCH_DEF_ERROR,
			noErrors: false,
			errors: [useNonHookTrans().trans("fetchError.error.exception", {
				method,
				path,
				error: exc.message,
			})],
		};
	}
}

export async function fetchFile<T = any>(path: string, fetchOptions?: any, options?: IFetchJsonOptions): Promise<IFetchFileOutput<T>> {
	const method = "GET";
	const correlationId = uuidv4();
	let request = null;

	try {
		request = await fetch(path, {
			headers: {
				"Accept": 'application/json',
				"X-Correlation-Id": correlationId,
				"X-Instance-Id": instanceId,

				...method === "GET"
					? {}
					: {
						"Content-Type": "application/json",
					},
			},
			credentials: 'include',
			signal: abortFetchSignal(REQUEST_TIMEOUT),
			...fetchOptions,
		});

		const statusCode = request.status;

		if (statusCode === 200 && request.headers.get("Content-Type").indexOf("application/json") === -1) {
			const blob = await request.blob();

			return {
				data: blob,
				fileName: request.headers.get("Content-Disposition").split("filename=")[1],
				status: statusCode,
				noErrors: true,
				errors: [],
			};
		}

		const jsonData = await request.json();

		const errors = getErrors({
			status: request.status,
			path,
			errors: jsonData.errors || [],
			requestData: jsonData,
			pretty422: options?.pretty422,
		});

		return {
			data: jsonData,
			fileName: null,
			status: request.status,
			noErrors: errors.length === 0,
			errors,
		};
	} catch (exc) {
		/* eslint-disable no-console */
		return {
			data: null,
			fileName: null,
			status: request?.status || FETCH_DEF_ERROR,
			noErrors: false,
			errors: [useNonHookTrans().trans("fetchError.error.exception", {
				method,
				path,
				error: exc.message,
			})],
		};
	}
}

/**
 * Combines strings and/or objects into one single string usable as a value for the className property
 */
export const classNames = (classes: Array<string | { [key: string]: boolean }> = []): string => (Array.isArray(classes)
	? classes
	: [classes]).reduce((cls, cl) => {
	// an empty string will still pass - it is faster and breaks nothing
	if (typeof cl === 'string') {
		cl && cls.push(cl);
	} else {
		// every truthy property is included in the final className
		cl && Object.keys(cl).forEach(key => cl[key] && cls.push(key));
	}

	return cls;
}, <Array<string>>[]).join(' ');


export function removeFromArray<T>(inputArray: Array<T>, compareFn: (value: T) => boolean): Array<T> {
	const ind = inputArray.findIndex(compareFn);

	if (ind !== -1) {
		const copiedArray = inputArray.slice();

		copiedArray.splice(ind, 1);

		return copiedArray;
	}

	return inputArray;
}

export function compareArray<T>(arrayA: Array<T>, arrayB: Array<T>): boolean {
	const len = arrayA.length;

	if (len !== arrayB.length) {
		return false;
	} else if (!len) {
		return true;
	}

	let areSame = false;

	arrayA.every((itemA, ind) => {
		const itemB = arrayB[ind];

		areSame = itemA === itemB;

		return areSame;
	});

	return areSame;
}

export function checkIPAddress(ipAddress: string): boolean {
	// todo - zrusit exact?
	return ipRegex({ exact: true }).test(ipAddress);
}

export function checkReferers(url: string): boolean {
	const isError = url.length < 3
		|| url.indexOf("/") !== -1
		|| url.indexOf("://") !== -1;

	return isError === false;
}

// https://github.com/manishsaraan/email-validator/blob/master/index.js
export function checkEmail(email: string): boolean {
	if (!email) {
		return false;
	}

	const emailParts = email.split('@');

	if (emailParts.length !== 2) {
		return false;
	}

	const account = emailParts[0];
	const address = emailParts[1];

	if (account.length > 64) {
		return false;
	} else if (address.length > 255) {
		return false;
	}

	const domainParts = address.split('.');

	if (domainParts.some(part => part.length > 63)) {
		return false;
	}

	/* eslint-disable-next-line */
	const tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/u;

	return tester.test(email);
}

export function checkEmails(emails: string) {
	const parts = emails.split(",");

	for (const part of parts) {
		const email = part.trim();

		if (!checkEmail(email)) {
			return false;
		}
	}

	return true;
}

export function getId(): number {
	return Math.random() * 1000 >>> 0;
}

export function getParams(params: {[key: string]: string}) {
	const output: IUrlParams = {
		projectId: 0,
		projectInvitationId: "",
		walletInvitationId: "",
		keyId: 0,
	};

	Object.keys(ROUTE_PARAMS).forEach(key => {
		const item = ROUTE_PARAMS[key];
		const name = item.replace(":", "");
		const value = params[name];

		if (value) {
			switch (item) {
				case ROUTE_PARAMS.PROJECT_INVITATION_ID:
					output.projectInvitationId = value;
					break;

				case ROUTE_PARAMS.WALLET_INVITATION_ID:
					output.walletInvitationId = value;
					break;

				case ROUTE_PARAMS.KEY_ID: {
					const testValue = parseFloat(value);

					if (!isNaN(testValue)) {
						output.keyId = testValue;
					}
				}
					break;

				case ROUTE_PARAMS.PROJECT_ID: {
					const testValue = parseFloat(value);

					if (!isNaN(testValue)) {
						output.projectId = testValue;
					}
				}
					break;

				default:
			}
		}
	});

	return output;
}

export function getRestrictionType(apiKey: IClientKey): TRestrictionsTypes {
	const keys = Object.keys(apiKey.restrictions);

	if (keys.length) {
		const name = keys[0];

		switch (name) {
			case "ipAddresses":
			case "referers":
			case "userAgents":
				return name;
			default:
		}
	}

	return "";
}

export function getRestrictionValue(apiKey: IClientKey): Array<string> {
	const keys = Object.keys(apiKey.restrictions);

	if (keys.length) {
		return apiKey.restrictions[keys[0]].slice();
	}

	return [];
}

export function getSearchParams(params: {[key: string]: string}): string {
	const usp = new URLSearchParams();

	Object.keys(params).forEach(name => usp.append(name, params[name]));

	return `?${usp.toString()}`;
}

export function getClientKeyChangeData(keyData: IClientKey): IClientKeyUpdate {
	const { id, internalName, apiKey, createdAt, canConsume, forbiddenConsumptionReason, ...rest } = keyData;

	return {
		...rest,
		lastUpdate: "",
	};
}

export function getAdminKeyChangeData(keyData: IAdminKey): IAdminKeyUpdate {
	const { id, internalName, apiKey, createdAt, canConsume, forbiddenConsumptionReason, ...rest } = keyData;

	return {
		...rest,
		lastUpdate: "",
	};
}

// gt - greather than
// eq - equal
// eg - equal greather
export interface IErrorItem {
	check: "string-gt0" | "string-gt1" | "string-gt5" | "string-gt9" | "string-2-35" | "string-5-50" | "string-10-50" | "string-50-500" | "string-int-desc"
		| "string-user-desc" | "number-gt0" | "number-eg0" | "number-bigint" | "email" | "emails" | "url" | "project-url" | "number-null"
		| "internal-id" | "custom" | "string-user-note";
	msg: string;
	custom?: (value: any, name: string) => boolean;
}

export function checkForErrorsState<T, Y>(compareData: {[K in keyof T]: T[K]}, errorsObj: {[key in keyof Partial<T>]: IErrorItem | Array<IErrorItem> }) {
	const errorsState: Y = {} as Y;
	let errorsCount = 0;

	Object.keys(errorsObj).forEach(key => {
		// vynulujeme
		errorsState[key] = "";

		if (key in compareData) {
			const cfgData = errorsObj[key];
			const allConfigs: Array<IErrorItem> = Array.isArray(cfgData) ? cfgData : [cfgData];
			const value = compareData[key];

			allConfigs.forEach(cfg => {
				let errorCheck = false;

				switch (cfg.check) {
					case "number-gt0":
						errorCheck = isNaN(value as any) || value <= 0;
						break;

					case "number-eg0":
						errorCheck = isNaN(value as any) || value < 0;
						break;

					case "number-bigint":
						errorCheck = isNaN(value as any) || value < 0 || value > 20000000000;
						break;

					case "string-gt0":
						errorCheck = typeof value !== "string" || !value;
						break;

					case "string-gt1":
						errorCheck = typeof value !== "string" || value.length < 2;
						break;

					case "string-gt5":
						errorCheck = typeof value !== "string" || value.length < 5;
						break;

					case "string-gt9":
						errorCheck = typeof value !== "string" || value.length < 10;
						break;

					case "string-user-desc":
						errorCheck = typeof value !== "string" || value.length > MAX_USER_DESCIPTION_LEN;
						break;

					case "string-user-note":
						errorCheck = typeof value !== "string" || value.length > MAX_USER_NOTE_LEN[1] || value.length < MAX_USER_NOTE_LEN[0];
						break;

					case "string-int-desc":
						errorCheck = typeof value !== "string" || value.length > MAX_INT_DESCIPTION_LEN;
						break;

					case "string-2-35":
						errorCheck = typeof value !== "string" || value.length < 2 || value.length > 35;
						break;

					case "string-5-50":
						errorCheck = typeof value !== "string" || value.length < 5 || value.length > 50;
						break;

					case "string-10-50":
						errorCheck = typeof value !== "string" || value.length < 10 || value.length > 50;
						break;

					case "string-50-500":
						errorCheck = typeof value !== "string" || value.length < 50 || value.length > 500;
						break;

					case "custom":
						errorCheck = typeof cfg.custom === "function" ? cfg.custom(value, key) : false;
						break;

					case "email":
						errorCheck = !value || !checkEmail(value as string);
						break;

					case "emails":
						errorCheck = !value || !checkEmails(value as string);
						break;

					case "url":
						errorCheck = value.toLowerCase().indexOf("http") === -1 || !urlRegexSafe({ exact: true }).test(value);
						break;

					case "project-url": {
						errorCheck = !urlRegexSafe({ exact: true }).test(value) || value.length < 4 || value.length > 250;
						break;
					}

					case "internal-id":
						// ID projektu musí být složeno s malých písmen, číslic nebo pomlček. Musí začínat písmenem a končit písmenem nebo číslem
						errorCheck = value.match(/[A-Z]/u) !== null
							|| value.match(/^[a-z]/u) === null
							|| value.match(/[a-z0-9]$/u) === null;
						break;

					case "number-null":
						errorCheck = value !== null && isNaN(value);
						break;

					default:
				}

				if (errorCheck) {
					errorsState[key] = cfg.msg;
					errorsCount++;
				}
			});
		} else {
			errorsState[key] = `Missing key ${key} in compareData`;
			errorsCount++;
		}
	});

	return {
		errorsState,
		errorsCount,
	};
}

export function getServiceName(service: IService): string {
	return service.service;
}

export function getApiPath(type: "client" | "admin"): string {
	if (type === "client") {
		return API_PATH.CLIENT;
	} else if (type === "admin") {
		return API_PATH.ADMIN;
	}

	return "";
}

export function getSemaphoreColor(consumptionTime: number, date: string | null): string {
	if (date) {
		const dateObj = new Date(date);
		const curObj = new Date();

		curObj.setDate(curObj.getDate() - consumptionTime);

		if (dateObj.getTime() >= curObj.getTime()) {
			return "green";
		}

		return "orange";
	}

	return "#c01";
}

export function getRandomItem<T>(inputArray: Array<T>): T {
	const min = 0;
	const max = inputArray.length;
	const randomInd = Math.floor(Math.random() * (max - min)) + min;

	return inputArray[randomInd];
}

export function formatBillingHistoryRange(billingHistory: IProjectBillingHistory, isCzech: boolean): string {
	return `${formatDate(new Date(billingHistory.billingPeriodFrom), isCzech)} - ${formatDate(new Date(billingHistory.billingPeriodTill), isCzech)}`;
}

export function formatReportRange(report: IReportItem, isCzech: boolean): string {
	return `${formatDate(new Date(report.periodStart), isCzech)} - ${formatDate(new Date(report.periodEnd), isCzech)}`;
}

export function parseNumber(value: string, minValue = 0): number {
	// zrusime - pro vsechny pripady
	const strValue = value.replace("-", "");
	const numValue = strValue
		? parseFloat(strValue)
		: minValue;

	return isNaN(numValue)
		? minValue
		: numValue;
}

export function formatNumberToThousands(value: number) {
	const strValue = value.toString();
	const parts = strValue.split(".");

	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/ug, ' ');

	return parts.join(".");
}

export function sortProjects(items: Array<IAdminProject | IClientProject>): Array<IAdminProject | IClientProject> {
	const sortedItems = items.slice();

	sortedItems.sort((aItem, bItem) => {
		const aDate = new Date(aItem.createdAt).getTime();
		const bDate = new Date(bItem.createdAt).getTime();

		return bDate - aDate;
	});

	return sortedItems;
}

export function getValidTill(project: IAdminProject) {
	return project.validTill.indexOf(MAX_YEAR.toString()) === -1 ? project.validTill : "";
}

export function pricePerCreditBE2FE(price: number) {
	return price * PRICE_CREDIT_RATIO / PENNY_KC_RATIO;
}

export function pricePerCreditFE2BE(price: number) {
	return price * PENNY_KC_RATIO / PRICE_CREDIT_RATIO;
}

export function compactObj(obj: Record<string, never>, count: number) {
	const output = {};

	Object.keys(obj).slice(0, count).forEach(key => {
		output[key] = obj[key];
	});

	return output;
}

export function getUrlHostname(url: string) {
	if (url.indexOf("http") === -1) {
		return url;
	}

	const urlObj = new URL(url);

	return urlObj.origin;
}

export function fillUrl(url: string) {
	return url.indexOf("http") === -1
		? `https://${url}`
		: url;
}

export function getReportCallKeys(report: IReportItem) {
	const keys = Object.keys(report).filter(key => key.startsWith("totalV1") || key.startsWith("totalSznV1"));

	keys.sort((aItem, bItem) => aItem.localeCompare(bItem));

	return keys;
}

export function getReportCallTitle(key: string) {
	const middlePart = key.replace(/^total/u, "").replace(/Calls$/u, "");
	const parts = middlePart.split(/(?=[A-Z])/u);
	const title = parts.join(" ");

	return title;
}

// neprekladat! - admin
export function getReportItems(type: TReportsTableShowData, report: IReportItem) {
	const items: Array<{ title: string; count: number; }> = [];

	switch (type) {
		case "projects":
			items.push({
				title: "Základní projekty",
				count: report.projectsBasicWithConsumption,
			});
			items.push({
				title: "Zvýhodněné projekty",
				count: report.projectsAdvancedWithConsumption,
			});
			items.push({
				title: "Projekty Na míru",
				count: report.projectsVipWithConsumption,
			});
			break;

		case "call":
			getReportCallKeys(report).forEach(keyName => {
				// totalV1MaptilesCalls -> V1 Maptiles
				// totalSznV1MaptilesCalls -> Szn V1 Maptiles

				const title = getReportCallTitle(keyName);

				items.push({ title, count: report[keyName] });
			});
			break;

		case "amount":
			items.push({
				title: "Peněženka",
				count: report.amountViaWallet / PENNY_KC_RATIO,
			});
			items.push({
				title: "Media plán",
				count: report.amountViaSos / PENNY_KC_RATIO,
			});
			items.push({
				title: "Barter",
				count: report.amountViaBarter / PENNY_KC_RATIO,
			});
			break;

		default:
	}

	// seradime
	items.sort((aItem, bItem) => bItem.count - aItem.count);

	return items.map(item => ({
		title: item.title,
		subTitle: `${formatNumberToThousands(item.count)}${type === "amount" ? " Kč" : ""}`,
	}));
}

export function deferPromise() {
	let resolveCb: () => void = null;
	let rejectCb: (error: string) => void = null;
	const promise: Promise<{ error: string}> = new Promise(resolve => {
		resolveCb = () => {
			resolve({
				error: "",
			});
		};
		rejectCb = (error: string) => {
			resolve({
				error,
			});
		};
	});

	return {
		promise,
		resolveCb,
		rejectCb,
	};
}

export function escapeTags(str: string) {
	return str.replace(/</gu, "&lt;").replace(/>/gu, "&gt;").replace(/&/gu, "&amp;");
}

export function getStrong(value: string): string {
	return `<strong>${escapeTags(value)}</strong>`;
}

export function isLocalhost() {
	return location.host.indexOf("localhost") !== -1 || location.host.indexOf("local.") !== -1;
}

export function getDateIsoString(date: Date): string {
	if (!date) {
		return "";
	}

	return [
		date.getFullYear(),
		date.getMonth() + 1,
		date.getDate(),
	].map(item => item.toString().padStart(2, "0")).join("-");
}

export function getMaxLogValue(value: number) {
	const log = Math.log10(value);
	const divider = Math.pow(10, log >>> 0);
	const step = value / divider;

	return Math.ceil(step) * divider;
}

export function downloadBlob(blob: Blob, fileName = "fileName") {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = url;
	link.setAttribute("download", fileName);

	// Append to html link element page
	document.body.appendChild(link);

	// Start download
	link.click();

	// Clean up and remove the link
	link.parentNode.removeChild(link);
}

export function getProjectsAndKeysLink(lang: string) {
	return CREATE_PROJECT_INFO_LINKS[lang];
}

export function getCatalogLink(lang: string) {
	return CATALOG_LINKS[lang];
}

export function getRegisterLink(lang: string) {
	return `https://registrace.seznam.cz?lang=${lang}`;
}

export function getHowToRegisterLink(lang: string) {
	if (lang === ALL_LANGS.czech || lang === ALL_LANGS.slovak) {
		return "https://developer.mapy.cz/portal-muj-ucet/jak-se-prihlasit/";
	}

	return "https://developer.mapy.cz/en/my-account/how-to-log-in/";
}

export function parseQuickComment(comment: string): IQuickCommentParseData {
	const output: IQuickCommentParseData = {
		apiKeysIds: (comment.match(API_KEY_REGEX) || []).map(apiKey => apiKey.replace("apikey=", "")),
		apiKeysNames: [],
		emails: (comment.match(EMAIL_REGEX) || []).filter(email => !BLACK_LIST_EMAILS.includes(email.toLowerCase())),
		empty: true,
	};

	const multiple = comment.match(/Id Projektu\/API klíče\n[^\n]+/ug);

	if (multiple) {
		for (const item of multiple) {
			const valuePart = item.split("\n")[1] || "";

			if (valuePart.includes(":")) {
				output.apiKeysNames.push(valuePart.trim());
			}
		}
	}

	output.empty = output.apiKeysIds.length === 0 && output.apiKeysNames.length === 0 && output.emails.length === 0;

	return output;
}
